import React, {useEffect, useState} from "react";
import {apiRequestWithAuth} from "../../utils/api";
import PageMeta from "../../components/common/PageMeta";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import {EditIcon} from "../../icons";
import {DynamicBlock} from "../../types/DynamicBlock";
import {useNavigate, useSearchParams} from "react-router-dom";
import {
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
} from "../../components/ui/table";

const DynamicBlocksList = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [pageId, setPage] = useState(() => {
        return parseInt(searchParams.get('page') || '1', 10);
    });
    const [hasResult, setHasResult] = useState(true);
    const [dynamicBlocks, setDynamicBlocks] = useState<DynamicBlock[]>([]);
    const [confirmDeleteId, setConfirmDeleteId] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    const loadDynamicBlocks = async () => {
        try {
            const res = await apiRequestWithAuth('/dynamic-blocks?page=' + pageId, 'GET', {});
            if (res.messageCode === 200) {
                setDynamicBlocks(res.result);
                if (res.result.length === 20) {
                    setHasResult(true);
                } else {
                    setHasResult(false);
                }
            } else {
                setDynamicBlocks([]);
                setHasResult(false);
            }
            setLoading(false);
        } catch (error) {
            console.error("Failed to load dynamic blocks:", error);
            setLoading(false);
        }
    };

    useEffect(() => {
        loadDynamicBlocks();
    }, [pageId]);

    const handlePageChange = (newPage: React.SetStateAction<number>) => {
        setPage(newPage);
        setSearchParams({ page: newPage.toString() });
    };

    const handleDelete = async (id: number) => {
        try {
            const res = await apiRequestWithAuth('/dynamic-blocks/' + id, 'DELETE', {});

            if (res.messageCode === 200) {
                setDynamicBlocks((prev) =>
                    prev.filter((block) => block.id !== id)
                );
                window.alert("Xóa thành công");
            }
        } catch (err) {
            console.error("Xóa thất bại:", err);
        }
    };

    if (loading) return <p>Loading dynamic blocks...</p>;

    return (
        <>
            <div className="p-6">
                <PageMeta title="Dynamic Blocks" description="Quản lý Dynamic Blocks"/>
                <PageBreadcrumb pageTitle="Dynamic Blocks"/>
            </div>
            <div className="p-6 max-w-10xl mx-auto">
                <button
                    className="mb-4 bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-300"
                    onClick={() => navigate("/dynamic-blocks/create")}
                >
                    + Add Dynamic Block
                </button>

                <div
                    className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
                    <div className="max-w-full overflow-x-auto">
                        <div className="min-w-[1102px]">
                            <Table>
                                {/* Table Header */}
                                <TableHeader className="border-b border-gray-100 dark:border-white/[0.05]">
                                    <TableRow>
                                        <TableCell
                                            isHeader
                                            className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400 text-nowrap"
                                        >
                                            ID
                                        </TableCell>
                                        <TableCell
                                            isHeader
                                            className="px-5 py-3 font-medium text-gray-500 text-center text-theme-xs dark:text-gray-400 text-nowrap"
                                        >
                                            STT
                                        </TableCell>
                                        <TableCell
                                            isHeader
                                            className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400 text-nowrap"
                                        >
                                            Tên block
                                        </TableCell>
                                        <TableCell
                                            isHeader
                                            className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400 text-nowrap"
                                        >
                                            Khóa học
                                        </TableCell>
                                        <TableCell
                                            isHeader
                                            className="px-5 py-3 font-medium text-gray-500 text-center text-theme-xs dark:text-gray-400 text-nowrap"
                                        >
                                            Trạng thái
                                        </TableCell>
                                        <TableCell
                                            isHeader
                                            className="px-5 py-3 font-medium text-gray-500 text-end text-theme-xs dark:text-gray-400 text-nowrap"
                                        >
                                            Hành động
                                        </TableCell>
                                    </TableRow>
                                </TableHeader>

                                {/* Table Body */}
                                <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                                    {dynamicBlocks.map((block) => (
                                        <TableRow key={block.id}>
                                            <TableCell
                                                className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                                {block.id}
                                            </TableCell>
                                            <TableCell
                                                className="px-4 py-3 text-gray-500 text-center text-theme-sm dark:text-gray-400">
                                                {block.orderNo || 0}
                                            </TableCell>
                                            <TableCell className="px-5 py-4 sm:px-6 text-start">
                                                <div className="flex items-center gap-3">
                                                    <div>
                                                        <span
                                                            className="block font-medium text-gray-800 text-theme-sm dark:text-white/90">
                                                            {block.name}
                                                        </span>
                                                        {block.shortDescription && (
                                                            <span className="block text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                                {block.shortDescription}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </TableCell>
                                            <TableCell
                                                className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                                {block.courses && block.courses.length > 0 ? (
                                                    <div className="flex flex-wrap gap-1">
                                                        {block.courses.map((course: any, index: number) => (
                                                            <span
                                                                key={index}
                                                                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-300"
                                                            >
                                                                {course.title}
                                                            </span>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    "-"
                                                )}
                                            </TableCell>
                                            <TableCell
                                                className="px-4 py-3 text-gray-500 text-center text-theme-sm dark:text-gray-400">
                                                <span
                                                    className={`inline-flex items-center px-2.5 py-0.5 justify-center gap-1 rounded-full font-medium text-theme-xs
                                                    ${block.statusText == 'Active' ? 'bg-success-50 text-success-600 dark:bg-success-500/15 dark:text-success-500' : 'bg-error-50 text-error-600 dark:bg-error-500/15 dark:text-error-500'}`}>
                                                    {block.statusText}
                                                </span>
                                            </TableCell>
                                            <TableCell className="px-4 py-3 text-end">
                                                <button
                                                    onClick={() => navigate(`/dynamic-blocks/edit/${block.id}`)}
                                                    className="text-sm px-1.5 py-1.5 rounded-md bg-indigo-100 text-indigo-600 hover:bg-indigo-200 transition mr-2">
                                                    <EditIcon/>
                                                </button>
                                                <button
                                                    onClick={() => setConfirmDeleteId(block.id)}
                                                    className="text-sm px-1.5 py-1.5 rounded-md bg-red-100 text-red-600 hover:bg-red-200 transition">
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor"
                                                         viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round"
                                                              strokeWidth={2}
                                                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                                                    </svg>
                                                </button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            <div className="p-4 border-t border-gray-100 dark:border-gray-800 sm:p-6">
                                <div className="space-y-6">
                                    <div className="flex items-center justify-between gap-8 sm:justify-between">
                                        <button
                                            disabled={pageId === 1}
                                            onClick={() => handlePageChange(pageId - 1)}
                                            className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-700 shadow-theme-xs hover:bg-gray-50 hover:text-gray-800 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-white/[0.03] dark:hover:text-gray-200 sm:px-3.5 sm:py-2.5"
                                        >
                                            <svg className="fill-current" width="20" height="20" viewBox="0 0 20 20"
                                                 fill="none"
                                                 xmlns="http://www.w3.org/2000/svg">
                                                <path fillRule="evenodd" clipRule="evenodd"
                                                      d="M2.58203 9.99868C2.58174 10.1909 2.6549 10.3833 2.80152 10.53L7.79818 15.5301C8.09097 15.8231 8.56584 15.8233 8.85883 15.5305C9.15183 15.2377 9.152 14.7629 8.85921 14.4699L5.13911 10.7472L16.6665 10.7472C17.0807 10.7472 17.4165 10.4114 17.4165 9.99715C17.4165 9.58294 17.0807 9.24715 16.6665 9.24715L5.14456 9.24715L8.85919 5.53016C9.15199 5.23717 9.15184 4.7623 8.85885 4.4695C8.56587 4.1767 8.09099 4.17685 7.79819 4.46984L2.84069 9.43049C2.68224 9.568 2.58203 9.77087 2.58203 9.99715C2.58203 9.99766 2.58203 9.99817 2.58203 9.99868Z"
                                                      fill=""></path>
                                            </svg>
                                            <span className="hidden sm:inline"> Previous </span>
                                        </button>
                                        <button
                                            disabled={!hasResult}
                                            onClick={() => handlePageChange(pageId + 1)}
                                            className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-700 shadow-theme-xs hover:bg-gray-50 hover:text-gray-800 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-white/[0.03] dark:hover:text-gray-200 sm:px-3.5 sm:py-2.5">
                                            <span className="hidden sm:inline"> Next </span>
                                            <svg className="fill-current" width="20" height="20" viewBox="0 0 20 20"
                                                 fill="none"
                                                 xmlns="http://www.w3.org/2000/svg">
                                                <path fillRule="evenodd" clipRule="evenodd"
                                                      d="M17.4165 9.9986C17.4168 10.1909 17.3437 10.3832 17.197 10.53L12.2004 15.5301C11.9076 15.8231 11.4327 15.8233 11.1397 15.5305C10.8467 15.2377 10.8465 14.7629 11.1393 14.4699L14.8594 10.7472L3.33203 10.7472C2.91782 10.7472 2.58203 10.4114 2.58203 9.99715C2.58203 9.58294 2.91782 9.24715 3.33203 9.24715L14.854 9.24715L11.1393 5.53016C10.8465 5.23717 10.8467 4.7623 11.1397 4.4695C11.4327 4.1767 11.9075 4.17685 12.2003 4.46984L17.1578 9.43049C17.3163 9.568 17.4165 9.77087 17.4165 9.99715C17.4165 9.99763 17.4165 9.99812 17.4165 9.9986Z"
                                                      fill=""></path>
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Delete Confirmation Modal */}
                {confirmDeleteId && (
                    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
                        <div className="bg-white p-6 rounded shadow-md w-full max-w-md">
                            <h2 className="text-lg font-semibold mb-4">Xác nhận xóa</h2>
                            <p className="mb-6">Bạn có chắc chắn muốn xóa dynamic block này không?</p>
                            <div className="flex justify-end gap-3">
                                <button
                                    className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400 transition"
                                    onClick={() => setConfirmDeleteId(null)}
                                >
                                    Hủy
                                </button>
                                <button
                                    className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition"
                                    onClick={() => {
                                        handleDelete(confirmDeleteId);
                                        setConfirmDeleteId(null);
                                    }}
                                >
                                    Xóa
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default DynamicBlocksList;

